// Image URL optimizers.
//
// Two providers coexist:
//   1. Legacy Cloudinary URLs — served from `res.cloudinary.com`. We inject
//      the `f_auto,q_auto,c_fill,ar_1:1,g_auto,w_N,h_N` transformation.
//   2. New Bunny CDN URLs — served from our Pull Zone (default host suffix
//      `.b-cdn.net`, or a custom domain like `cdn.vvslabjewelry.com`). We
//      append `?width=N&format=webp` (Bunny Optimizer).
//
// Any other URL (Unsplash, Pexels, direct assets) is returned unchanged —
// the container's `aspect-square` + `object-cover` CSS keeps it looking 1:1.

const CLD_MARKER = "res.cloudinary.com";
const UPLOAD_SPLIT = "/image/upload/";

const isCloudinary = (url) => url.includes(CLD_MARKER) && url.includes(UPLOAD_SPLIT);
// Match `<anything>.b-cdn.net` (Bunny default) OR the explicit pull-zone host
// we injected at build time. Bunny Optimizer honours ?width=&format=.
const BUNNY_HOST = (process.env.REACT_APP_BUNNY_CDN_HOST || "vvslab.b-cdn.net").toLowerCase();
const isBunny = (url) => {
  const lower = url.toLowerCase();
  return lower.includes(".b-cdn.net/") || lower.includes(`//${BUNNY_HOST}/`);
};

function withBunnyOptim(url, width, { square = false, format = "webp" } = {}) {
  const hasQuery = url.includes("?");
  const params = [];
  if (width && Number.isFinite(width)) params.push(`width=${Math.round(width)}`);
  if (square && width && Number.isFinite(width)) {
    // For 1:1 tiles we also constrain height so Bunny Optimizer crops.
    params.push(`height=${Math.round(width)}`, "aspect_ratio=1:1");
  }
  if (format) params.push(`format=${format}`);
  if (!params.length) return url;
  return `${url}${hasQuery ? "&" : "?"}${params.join("&")}`;
}

// Auto-format + quality + responsive width. Preserves aspect ratio.
export function cldOpt(url, width) {
  if (!url || typeof url !== "string") return url || undefined;
  if (isCloudinary(url)) {
    const [head, tail] = url.split(UPLOAD_SPLIT);
    if (/^[a-z]+_[^/]+\//i.test(tail)) return url;
    const parts = ["f_auto", "q_auto"];
    if (width && Number.isFinite(width)) parts.push(`w_${Math.round(width)}`, "c_limit");
    return `${head}${UPLOAD_SPLIT}${parts.join(",")}/${tail}`;
  }
  if (isBunny(url)) return withBunnyOptim(url, width);
  return url;
}

// Perfect 1:1 square crop delivery.
export function cldSquare(url, size = 800) {
  if (!url || typeof url !== "string") return url || undefined;
  const w = Math.max(80, Math.round(size));
  if (isCloudinary(url)) {
    const [head, tail] = url.split(UPLOAD_SPLIT);
    if (/^[a-z]+_[^/]+\//i.test(tail)) return url;
    const parts = ["f_auto", "q_auto", "c_fill", "g_auto", "ar_1:1", `w_${w}`, `h_${w}`];
    return `${head}${UPLOAD_SPLIT}${parts.join(",")}/${tail}`;
  }
  if (isBunny(url)) return withBunnyOptim(url, w, { square: true });
  return url;
}
